import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

interface Ad {
  id: number;
  title: string;
  banner_url: string;
  redirect_url: string;
  placement: 'dashboard_top' | 'practice_sidebar' | 'leaderboard_bottom';
  device: 'all' | 'desktop' | 'mobile';
}

interface AdBannerProps {
  placement: 'dashboard_top' | 'practice_sidebar' | 'leaderboard_bottom';
}

export default function AdBanner({ placement }: AdBannerProps) {
  const [ads, setAds] = useState<Ad[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkWidth = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkWidth();
    window.addEventListener('resize', checkWidth);

    const fetchAds = async () => {
      try {
        const response = await fetch('/wp-json/shuddhoshikha/v1/syllabus-ads');
        const data = await response.json();
        if (data.success && Array.isArray(data.ads)) {
          setAds(data.ads);
        }
      } catch (err) {
        console.error('Failed to load ads:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAds();

    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  if (loading) return null;

  const filteredAds = ads.filter(ad => {
    if (ad.placement !== placement) return false;
    if (ad.device === 'desktop' && isMobile) return false;
    if (ad.device === 'mobile' && !isMobile) return false;
    return true;
  });

  if (filteredAds.length === 0) return null;

  const activeAd = filteredAds[Math.floor(Math.random() * filteredAds.length)];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="w-full mt-4"
    >
      <a
        href={activeAd.redirect_url}
        target="_blank"
        rel="noopener noreferrer"
        className="block w-full overflow-hidden rounded-2xl border border-gray-100/50 dark:border-white/5 transition-transform hover:scale-[1.005] active:scale-[0.995] duration-300 shadow-sm hover:shadow-md"
      >
        <img
          src={activeAd.banner_url}
          alt={activeAd.title}
          loading="lazy"
          className="w-full h-auto object-cover max-h-[160px] md:max-h-[220px] block"
        />
      </a>
    </motion.div>
  );
}
